/** @jsxImportSource @emotion/react */
import { jsx, css } from "@emotion/react";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import MenuItem from "@mui/material/MenuItem";
import { GroupInfo, GroupMemberInfo } from "../types/gigya";
import { useTheme } from "@table-library/react-table-library/theme";
import {
  DEFAULT_OPTIONS,
  getTheme,
} from "@table-library/react-table-library/material-ui";
import { usePagination } from "@table-library/react-table-library/pagination";
import {
  Table,
  Header,
  HeaderRow,
  Body,
  Row,
  HeaderCell,
  Cell,
  Data,
  TableNode,
} from "@table-library/react-table-library/table";
import { useMobileDetector } from "../hooks";
import { useTranslation } from "react-i18next";
import { AccordionTableItem } from "./AccordianTableItem";
import {
  useGetMembers,
  useRemoveMember,
  useSetGroupInfo,
  useSetMemberInfo,
} from "../api";
import { SetStateAction, useEffect, useState } from "react";
import ModalRemoveMember from "./ModalRemoveMember";
import { GigyaGroupModels } from "../types/gigya.schema";
import { Button } from "@mui/material";

type SEARCHGROUPINFO = {
  groupId: string;
  model: string;
  groupData: {
    clinicName: string;
    priceGroup: string;
    country: string;
    city: string;
    SFHomeDeliveryOnly: boolean;
    industry: string;
    salesOrg: string;
    altID: string;
    payer: string;
    archived: boolean;
    salesZone: string;
    clinicType: string;
    customerClass: string;
    billTo: string;
    softClosed: boolean;
    state: string;
    billingZip: string;
    zip: string;
    companyCode: string;
    soldToClinicName: string;
    soldTo: string;
    customerGroup: string;
    customerPlanGroup: string;
    salesTerritory: string;
    accountType: string;
    approvedEmails: string;
    streetAddress: string;
    blockedClinic: boolean;
    paymentTerm: string;
    billingCity: string;
    shipTo: string;
    salesRegion: string;
    customerPriceProcedure: string;
    removedUsers?: string | string[];
  };
  created: string;
  lastUpdated: string;
  createdTimestamp: number;
  lastUpdatedTimestamp: number;
  membersCount: number;
};

type TableMembersProps = {
  myUID: string;
  groupID: string;
  group: GroupInfo<GigyaGroupModels> | SEARCHGROUPINFO | undefined;
};

type WithClinic = {
  groupId: string;
};

type GroupMemberInfoWithClinic = GroupMemberInfo & WithClinic;

// IMPLEMENT CONTEXT FOR CURRENT USER, MEMBERS, API METHODS
// IMPLEMENT REACT API
export const TableMembers = (props: TableMembersProps) => {
  const { myUID, groupID, group } = props;
  const LIMIT = 5;
  const { t } = useTranslation();
  const [offset, setOffset] = useState(0);
  const IS_ENUS = process.env.REACT_APP_LOCALE === "en-us";

  const {
    members,
    setMembers,
    setNewGroupId,
    loadingMembers,
    membersError,
    paginationStart,
    setPaginationStart,
    paginationLimit,
    paginationTotalCount,
  } = useGetMembers(myUID, groupID, IS_ENUS ? "" : "approved, rejected");
  const { memberInfoResp, setMemberInfo, loadingMemberInfo, memberInfoError } =
    useSetMemberInfo(myUID, groupID);
  const {
    removeMemberResp,
    setRemovedMemberInfo,
    loadingRemoveMember,
    removeMemberError,
  } = useRemoveMember(myUID, groupID);
  const {
    setGroupInfoId,
    setRemovedUsers,
    loadingGroupInfoResp,
    groupInfoRespError,
  } = useSetGroupInfo();

  const [removedMember, setRemovedMember] = useState<
    GroupMemberInfo | undefined
  >(undefined);
  const [isModalRemoveMemberOpen, setIsModalRemovedMemberOpen] =
    useState(false);
  const [dummyMember, setDummyMember] = useState<GroupMemberInfo>();
  const [replaceDummies, setReplaceDummies] = useState(false);
  const [paginatedMembers, setPaginatedMembers] = useState<GroupMemberInfo[]>(
    [],
  );
  const [membersData, setMembersData] = useState<Data<TableNode>>({
    nodes: [],
  });

  const [isChangesMade, setIsChangesMade] = useState(false);
  const [updatedMembers, setUpdatedMembers] = useState<
    GroupMemberInfoWithClinic[]
  >([]);

  const doGet = (params: {
    offset: SetStateAction<number>;
    insertDummies?: boolean;
    replaceDummies?: boolean;
  }) => {
    if (params.insertDummies) {
      // APPAEND ARRAY OF DUMMIES UP TO OFFSET - 1
      const dummiesArr: GroupMemberInfo[] = [];
      const numberOfDummiesNeeded =
        Number(params.offset) - paginatedMembers.length;
      for (let i = 0; i < numberOfDummiesNeeded; i++) {
        dummiesArr.push(dummyMember!);
      }
      setPaginatedMembers([...paginatedMembers, ...dummiesArr]);
      setPaginationStart(params.offset);
    } else if (params.replaceDummies) {
      // REPLACE DUMMIES STARTING AT OFFSET UNTIL
      // MEMBERS ARRAY IS EXHAUSTED
      setReplaceDummies(true);
      setOffset(params.offset);
      setPaginationStart(params.offset);
    } else {
      setPaginationStart(params.offset);
    }
  };

  useEffect(() => {
    // SET INITIAL PAGINATED MEMBERS AND DUMMY MEMBER
    if (members.length && paginatedMembers.length === 0) {
      setDummyMember({
        ...members[0],
        UID: "DUMMY",
        profile: {
          ...members[0].profile,
          lastName: "",
          firstName: "",
          email: "",
        },
        relationshipData: { ...members[0].relationshipData, role: "" },
      });
      setPaginatedMembers(members);
    }
    // REPLACE DUMMIES
    else if (members.length && paginatedMembers[offset].UID === "DUMMY") {
      const beginning = paginatedMembers.slice(0, offset);
      const end = paginatedMembers.slice(offset + LIMIT);
      setReplaceDummies(false);
      setPaginatedMembers([...beginning, ...members, ...end]);
    }
    // APPEND PAGE
    else {
      setPaginatedMembers([...paginatedMembers, ...members]);
    }
  }, [members]);

  // UPDATE MEMBERS DATA WHEN PAGINATED MEMBERS IS UPDATED
  useEffect(() => {
    const newMembersData = {
      nodes: paginatedMembers.map((node, index) => {
        return { ...node, id: index };
      }),
    };
    setMembersData(newMembersData);
  }, [paginatedMembers]);

  const onPaginationChange = (action: any, state: any) => {
    const offset = state.page * LIMIT;
    // IF NUMBER OF ITEMS IS GREATER THAN PAGE - 1 * LIMIT
    // AND THERE IS NO DUMMY AT PAGE * LIMIT
    const paginatedMembersArrLen = paginatedMembers.length;
    const newPaginatedMembersArrLen = offset + LIMIT;
    const previousPageArrLen = (state.page - 1) * LIMIT;

    // IF PAGINATED MEMBERS HAS THE CORRECT AMOUNT OF ITEMS
    // BUT A DUMMY VALUE EXIST ON THE PAGE SELECTED, DOGET
    // AND REPLACE THE DUMMY VALUES
    if (
      paginatedMembersArrLen > offset &&
      paginatedMembers[offset].UID === "DUMMY"
    ) {
      console.log("REPLACE: ");
      // REPLACE DUMMIES
      doGet({
        offset,
        replaceDummies: true,
      });
    }

    // IF PAGINATED MEMBERS + LIMIT IS GREATER THAN THE
    // MAXMIMUM NUMBER OF POSSIBLE MEMBERS OF THE SELECTED
    // PAGE, DO NOTHING. PREVENTS FETCHING+APPENDING
    // MEMBERS ON THE LAST PAGE WHEN WE ALREADY HAVE THEM
    else if (paginatedMembersArrLen + LIMIT > newPaginatedMembersArrLen) {
      // TODO
    }

    // IF PAGINATED MEMBERS HAS THE CORRECT AMOUNT OF ITEMS
    // AND NO DUMMY VALUE EXIST ON THE PAGE SELECTED,
    // RENDER THE NEW PAGE
    else if (
      paginatedMembersArrLen >= newPaginatedMembersArrLen &&
      paginatedMembers[offset].UID !== "DUMMY"
    ) {
      // TODO
    }

    // IF PAGINATED MEMBERS HAS TOO FEW ITEMS, DOGET THE NEW
    // PAGE AND INSERT DUMMY VALUES WHERE NECESSARY
    else if (paginatedMembersArrLen <= newPaginatedMembersArrLen - LIMIT * 2) {
      console.log("INSERT: ");
      doGet({
        offset,
        insertDummies: true,
      });
    } else {
      console.log("DOGET: ");
      doGet({
        offset,
      });
    }
    console.log("LENGTH: ", paginatedMembers.length);
  };

  const pagination = usePagination(membersData, {
    state: { page: 0, size: 5 },
    onChange: onPaginationChange,
  });

  const justifyIconStyle = css`
    display: flex;
    justify-content: flex-end;
  `;

  const customTheme = {
    Table: ``,
    Header: ``,
    Body: ``,
    HeaderCell: ``,
    HeaderRow: `
      font-family: Montserrat;
      font-size: 24px;
      font-style: normal;
      font-weight: 700;
      line-height: normal;

      & .th.border-remove {
        border-bottom: 0;
      }

      &.tr-header th {
        background-color: unset;
        font-weight: normal;
        text-align: unset;
        border: none;
        padding: 0;
      };
    `,
    BaseRow: `
      font-family: Montserrat;
      font-size: 16px;
      font-style: normal;
      font-weight: 700;
      line-height: 20px;
    `,
    Row: `
      &.td {
        border: none;
        padding: 0;
      };

      &.tr-body.team-management td {
        border-top: none;
        border-left: none;
        border-right: none;
        border-bottom: 1px solid #e0e0e0;
        padding: 0;
      };
    `,
    BaseCell: `
      padding: 0;
      margin-bottom: 15px;
      border: 0;
    `,
    Cell: `
      >div {
        white-space: normal;
        word-break: break-all;
      }
    `,
  };

  const cancelSaveRowStyles = css`
    display: flex;
    justify-content: flex-end;
  `;

  const handleStatusChange = (member: GroupMemberInfo, status: string) => {
    const updatedMember = {
      ...member,
      relationshipData: { ...member.relationshipData, status },
      groupId: groupID,
    };

    let updatedPaginatedMembers: SetStateAction<GroupMemberInfo[]> = [];

    const index = paginatedMembers.findIndex(
      (paginatedMember) => paginatedMember.UID === member.UID,
    );

    if (index !== -1) {
      updatedPaginatedMembers = [
        ...paginatedMembers.slice(0, index),
        updatedMember,
        ...paginatedMembers.slice(index + 1),
      ];
    }

    setPaginatedMembers(updatedPaginatedMembers);
    setUpdatedMembers((prevState) => [...prevState, updatedMember]);

    setIsChangesMade(true);
  };

  const handleRoleChange = (member: GroupMemberInfo, role: string) => {
    // No need to move or filter array here
    const updatedMember = {
      ...member,
      relationshipData: { ...member.relationshipData, role },
      groupId: groupID,
    };

    let updatedPaginatedMembers: SetStateAction<GroupMemberInfo[]> = [];

    const index = paginatedMembers.findIndex(
      (paginatedMember) => paginatedMember.UID === member.UID,
    );

    if (index !== -1) {
      updatedPaginatedMembers = [
        ...paginatedMembers.slice(0, index),
        updatedMember,
        ...paginatedMembers.slice(index + 1),
      ];
    }

    setPaginatedMembers(updatedPaginatedMembers);
    setUpdatedMembers((prevState) => [...prevState, updatedMember]);

    setIsChangesMade(true);
  };

  const makeStrArr = (arg: string | string[] | undefined) => {
    if (arg === undefined) {
      return [];
    }

    if (typeof arg === "string") {
      return [arg];
    }

    if (Array.isArray(arg)) {
      return arg;
    }

    return [];
  };

  const handleDelete = (member: GroupMemberInfo | undefined) => {
    if (member) {
      // removed members with membergroups
      if (group && group.groupData) {
        setRemovedUsers([
          ...new Set([...makeStrArr(group.groupData.removedUsers), member.UID]),
        ]);
      } else {
        setRemovedUsers([member.UID]);
      }

      const updatedPaginatedMembers = paginatedMembers.filter(
        (paginatedMember) => paginatedMember.UID !== member.UID,
      );

      const newGroupId = groupID;

      setPaginatedMembers(updatedPaginatedMembers);
      setIsModalRemovedMemberOpen(false);
      setRemovedMemberInfo({
        emailId: member.profile.email.toString(),
        groupId: newGroupId,
      });
    }
  };

  const handleRemoveMember = (member: GroupMemberInfo) => {
    setRemovedMember(member);
    setIsModalRemovedMemberOpen(true);
    setIsChangesMade(true);
  };

  const handleDialogClose = () => {
    setIsModalRemovedMemberOpen(false);
  };

  const theme = useTheme([getTheme(DEFAULT_OPTIONS), customTheme]);
  const { isMobile } = useMobileDetector();

  const renderName = (row: GroupMemberInfo) => {
    return `${row.profile.lastName}, ${row.profile.firstName}`;
  };

  const handleChangeStatus = (
    event: SelectChangeEvent,
    row: GroupMemberInfo,
  ) => {
    handleStatusChange(row, event.target.value);
  };

  const renderStatus = (row: GroupMemberInfo) => {
    return (
      <Select
        sx={{
          boxShadow: "none",
          ".MuiOutlinedInput-notchedOutline": { border: 0 },
          ".MuiSelect-select": { marginBottom: "20px" },
          minWidth: 120,
        }}
        id="member-status-id"
        value={row.relationshipData.status}
        label={row.relationshipData.status}
        onChange={(event) => handleChangeStatus(event, row)}
      >
        <MenuItem value="approved">{t("approved")}</MenuItem>
        <MenuItem value="rejected">{t("reject")}</MenuItem>
        <MenuItem
          value="pending"
          disabled={row.relationshipData.status === "rejected"}
        >
          {t("pendingRequest")}
        </MenuItem>
      </Select>
    );
  };

  const handleChangeRole = (event: SelectChangeEvent, row: GroupMemberInfo) => {
    handleRoleChange(row, event.target.value);
  };

  const renderRole = (row: GroupMemberInfo) => {
    if (myUID !== row.UID) {
      return (
        <Select
          sx={{
            boxShadow: "none",
            ".MuiOutlinedInput-notchedOutline": { border: 0 },
            ".MuiSelect-select": { marginBottom: "20px" },
            minWidth: 120,
          }}
          id="member-role-id"
          value={row.relationshipData.role}
          label={row.relationshipData.role}
          onChange={(event) => handleChangeRole(event, row)}
        >
          <MenuItem value="admin">{t("exported.Admin")}</MenuItem>
          {process.env.REACT_APP_LOCALE !== "en-us" && (
            <MenuItem value="employee">{t("exported.Employee")}</MenuItem>
          )}
          <MenuItem value="member">{t("exported.Member")}</MenuItem>
        </Select>
      );
    } else {
      return (
        <Select
          sx={{
            boxShadow: "none",
            ".MuiOutlinedInput-notchedOutline": { border: 0 },
            ".MuiSelect-select": { marginBottom: "20px" },
            minWidth: 120,
          }}
          id="member-role-id"
          value={row.relationshipData.role}
          label={row.relationshipData.role}
          onChange={(event) => handleChangeRole(event, row)}
        >
          <MenuItem value="admin">{t("exported.Admin")}</MenuItem>
        </Select>
      );
    }
  };

  const renderEmail = (row: GroupMemberInfo) => {
    return <div>{row.profile.email}</div>;
  };

  const renderRemove = (row: GroupMemberInfo) => {
    return (
      <div css={justifyIconStyle}>
        <IconButton
          aria-label="delete"
          onClick={() => handleRemoveMember(row)}
          disabled={row.relationshipData.role !== "member" && row.UID === myUID}
        >
          <DeleteIcon />
        </IconButton>
      </div>
    );
  };

  const handleCancel = () => {
    setPaginatedMembers(paginatedMembers);
    setUpdatedMembers([]);
    setIsChangesMade(false);
  };

  const handleSave = () => {
    setMemberInfo(
      updatedMembers.map((updatedMember) => ({
        memberUid: updatedMember.UID,
        role: updatedMember.relationshipData.role,
        status: updatedMember.relationshipData.status,
        groupId: updatedMember.groupId,
      })),
    );
    setUpdatedMembers([]);
    setIsChangesMade(false);
    setPaginatedMembers([...paginatedMembers]);
  };

  const renderPaginationButtons = (
    currentPage: number,
    totalPages: number,
    maxVisiblePages: number,
  ) => {
    const visiblePages = [];

    if (totalPages > 0) {
      visiblePages.push({ label: 1, page: 1 });
    }

    // Calculate start and end of the visible range
    let startPage = Math.max(2, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(
      totalPages - 1,
      currentPage + Math.floor(maxVisiblePages / 2),
    );

    // Adjust start and end page if needed
    if (endPage - startPage + 1 < maxVisiblePages) {
      if (startPage === 2) {
        endPage = Math.min(totalPages - 1, startPage + maxVisiblePages - 2);
      } else {
        startPage = Math.max(2, endPage - maxVisiblePages + 2);
      }
    }
    // Add ellipsis and pages in between
    if (startPage > 2) {
      visiblePages.push({
        label: "...",
        page: visiblePages[visiblePages.length - 1].page + 1,
      });
    }
    for (let i = startPage; i <= endPage; i++) {
      visiblePages.push({ label: i, page: i });
    }
    if (endPage < totalPages - 1) {
      visiblePages.push({
        label: "...",
        page: visiblePages[visiblePages.length - 1].page + 1,
      });
    }

    // Add last page
    if (totalPages > 1) {
      visiblePages.push({ label: totalPages, page: totalPages });
    }

    return visiblePages;
  };

  return (
    <>
      <ModalRemoveMember
        member={removedMember}
        isOpen={isModalRemoveMemberOpen}
        close={handleDialogClose}
        deleteMember={(memberToDel: GroupMemberInfo | undefined) =>
          handleDelete(memberToDel)
        }
        groupId={groupID || ""}
      />
      {isMobile && (
        <>
          <Table data={membersData} theme={theme} pagination={pagination}>
            {(nodes: any[]) => (
              <Body>
                {nodes.map((item, index) => (
                  <AccordionTableItem
                    key={index}
                    row={item}
                    onPropStatusChange={handleStatusChange}
                    onPropRoleChange={handleRoleChange}
                    remove={() => handleRemoveMember(item)}
                    removeDisabled={
                      item.relationshipData.role !== "member" &&
                      item.UID === myUID
                    }
                  />
                ))}
              </Body>
            )}
          </Table>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "50px",
            }}
          >
            <span>Total Pages: {Math.ceil(paginationTotalCount / 5)}</span>

            <span>
              Page:{" "}
              {renderPaginationButtons(
                pagination.state.page,
                Math.ceil(paginationTotalCount / 5),
                4,
              ).map((node: any, index: number) => (
                <button
                  key={index}
                  type="button"
                  style={{
                    fontWeight:
                      pagination.state.page === Number(node.page - 1)
                        ? "bold"
                        : "normal",
                  }}
                  onClick={() =>
                    pagination.fns.onSetPage(Number(node.page - 1))
                  }
                >
                  {node.label}
                </button>
              ))}
            </span>
          </div>
        </>
      )}
      {!isMobile && (
        <>
          <Table
            data={membersData}
            theme={theme}
            pagination={pagination}
            layout={{ isDiv: true }}
          >
            {(tableList: any[]) => (
              <>
                <Header>
                  <HeaderRow>
                    <HeaderCell className="border-remove">
                      {t("exported.Name")}
                    </HeaderCell>
                    {process.env.REACT_APP_LOCALE !== "en-us" && (
                      <HeaderCell className="border-remove">
                        {t("exported.Status")}
                      </HeaderCell>
                    )}
                    <HeaderCell className="border-remove">
                      {t("exported.Role")}
                    </HeaderCell>
                    <HeaderCell className="border-remove">
                      {t("exported.Email")}
                    </HeaderCell>
                    <HeaderCell className="border-remove"></HeaderCell>
                  </HeaderRow>
                </Header>
                <Body>
                  {tableList.map((item) => (
                    <Row className="team-management" key={item.id} item={item}>
                      <Cell>{renderName(item)}</Cell>
                      {process.env.REACT_APP_LOCALE !== "en-us" && (
                        <Cell>{renderStatus(item)}</Cell>
                      )}
                      <Cell>{renderRole(item)}</Cell>
                      <Cell>{renderEmail(item)}</Cell>
                      <Cell>{renderRemove(item)}</Cell>
                    </Row>
                  ))}
                  {tableList.length === 0 && (
                    <div>There are no resolved members</div>
                  )}
                </Body>
              </>
            )}
          </Table>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "50px",
            }}
          >
            <span>Total Pages: {Math.ceil(paginationTotalCount / 5)}</span>

            <span>
              Page:{" "}
              {renderPaginationButtons(
                pagination.state.page,
                Math.ceil(paginationTotalCount / 5),
                4,
              ).map((node: any, index: number) => (
                <button
                  key={index}
                  type="button"
                  style={{
                    fontWeight:
                      pagination.state.page === Number(node.page - 1)
                        ? "bold"
                        : "normal",
                  }}
                  onClick={() =>
                    pagination.fns.onSetPage(Number(node.page - 1))
                  }
                >
                  {node.label}
                </button>
              ))}
            </span>
          </div>
          <div css={cancelSaveRowStyles}>
            <Button
              variant="text"
              sx={{
                borderRadius: "4px",
                height: "35px",
              }}
              disabled={!isChangesMade}
              onClick={handleCancel}
            >
              {t("exported.Cancel")}
            </Button>
            <Button
              sx={{
                backgroundColor: "#1976d2",
                borderRadius: "4px",
                height: "35px",
              }}
              disabled={!isChangesMade}
              onClick={handleSave}
            >
              {t("exported.Save")}
            </Button>
          </div>
        </>
      )}
    </>
  );
};
